import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Box, Button, Card, IconButton, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { Fragment, SyntheticEvent, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useBlocker, useLocation } from 'react-router';
import { InferType } from 'yup';

import { WarningRounded } from '@mui/icons-material';
import {
  EmptyContent,
  enqueueSnackbar,
  FormProvider,
  FullPageSpinner,
  useBoolean,
  useDefaultEnumsStore,
  useDirtyFieldsStore,
  useFactFindErrorStore,
  useFactFindProcessStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import { AlertTriangleIcon, BenefitsInsuranceIcon, OthersAssetsIcon, PlusIcon, PrivateHealthInsuranceIcon } from '@zeta-core/assets';
import { AIInteractionService, IFactFindProcessStatus, InteractionSubSectionType, InvestorService, IPatchAIInteractionSectionRequest } from '@zeta-services';
import {
  FactFindType,
  IBenefitsAndInsurancePayload,
  IBenefitsAndInsuranceResponseApiResponse,
  IClientBeneficiary,
  IClientCardItem,
  IFactFindEntity,
  IViewfactFindBeneficiaryResponse,
  IViewFactFindRequest,
} from '@zeta-types';
import { ConflictsDialog } from '../../../components/conflicts-dialog';
import { getClientFeatureAccess } from '../../../helpers/access-control-helper';
import AllTranscripts from '../all-transcripts';
import { clientPartnerJointTabs, getLabel } from '../common/commons';
import FactFindHeading from '../common/fact-find-heading';
import ConfirmNavigationDialog from '../components/confirmation-dialog';
import ErrorDialog from '../components/error-dialog';
import { ErrorSection } from '../components/error-section';
import SaveDialog from '../components/save-dialog';
import SimpleFieldsTable from '../components/simple-fields-table';
import SuccessDialog from '../components/success-dialog';
import FactFindFooter from '../fact-find-layout/fact-find-footer';
import { useSourceControlContext } from '../fact-find-layout/sourcecontrol-context-component';
import Transcript from '../transcript';
import {
  beneficiariesAndInsuranceDefaultValues,
  beneficiariesAndInsuranceValidationSchema,
  EMPLOYEE_BENEFITS_TABLE_STRUCTURE,
  employeeBenefitsDefault,
  OTHER_BENEFITS_TABLE_STRUCTURE,
  otherBenefitsDefault,
  policiesDefault,
} from './form-helpers';
import PolicyCard from './policy-card';
import { createPayloadBeneficiariesAndInsurance, mapResponseToSchema } from './transform';

enum ISubSectionType {
  'private' = 'private',
  'employee' = 'employee',
  'other' = 'other',
}
export const BenefitsAndInsurance = ({
  isClient,
  from,
  refetch,
  clientData,
  conflictData,
  clientNomineesData,
  updatedClientPartnerDetails,
}: {
  isClient?: boolean;
  from: FactFindType;
  refetch?: () => void;
}) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const location = useLocation();
  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  let state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  if (isClient) {
    state = { clientData: { clientID: accessibleEntityID } };
  }
  const {
    data: sourceControlData,
    mutateAsync: sourceControlMutateAsync,
    isPending: sourceControlPending,
    clientPartnerDetails,
  } = useSourceControlContext() || {};

  type tabType = 'client' | 'partner' | 'joint';

  const [currentTab, setCurrentTab] = useState<tabType>('client');
  const [selectedSubSection, setSelectSubSection] = useState<string>(ISubSectionType.private);
  const [conflictsDialog, setConflictsDialog] = useState<boolean>(false);
  const isAllTranscripts = useBoolean();
  const isTranscript = useBoolean();
  const updateDirtyFieldsState = useDirtyFieldsStore((state) => state.updateDirtyFieldsState);
  const ffErrors = useFactFindErrorStore((state) => state?.ffErrors);
  const updateFFErrorStore = useFactFindErrorStore((state) => state?.updateFFErrorStore);
  const fieldErrors = ffErrors?.find((error) => error.section === InteractionSubSectionType.benefits_and_insurance)?.missing_fields;

  const defaultEnums = useDefaultEnumsStore((state) => state.defaultEnums);
  const insuranceTypeOptions = defaultEnums?.insuranceType?.map((e) => e.insuranceType) ?? [];
  const sectionsProcessDetails = useFactFindProcessStore((state) => state.sections);
  const currentSection = sectionsProcessDetails?.find((section) => section.section_name === InteractionSubSectionType.personal_details)?.status;

  const planningStatus = location?.state?.clientData?.planningStatus;
  const readOnly = getClientFeatureAccess(userAccess?.access, 'Advisor-Client-FF', planningStatus).isReadOnly;
  const isReadOnly =
    from === FactFindType.PARA_PLANNER ||
    currentSection === IFactFindProcessStatus.pending ||
    currentSection === IFactFindProcessStatus.started ||
    isClient ||
    readOnly;

  type beneficiariesAndInsuranceValidationSchemaType = InferType<typeof beneficiariesAndInsuranceValidationSchema>;

  const methods = useForm<beneficiariesAndInsuranceValidationSchemaType>({
    defaultValues: beneficiariesAndInsuranceDefaultValues,
    resolver: yupResolver(beneficiariesAndInsuranceValidationSchema),
  });

  const { reset } = methods;
  const [confirmNavigationModal, setConfirmNavigationModal] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const [beneficiariesList, setBeneficiariesList] = useState<IClientBeneficiary[] | null>(null);

  const isSuccess = useBoolean(false);
  const isError = useBoolean(false);

  const isDirtyInPage = methods.formState.isDirty;

  useEffect(() => {
    if (isDirtyInPage) {
      updateDirtyFieldsState(true);
    }
  }, [isDirtyInPage, updateDirtyFieldsState]);

  const blocker = useBlocker(({ nextLocation, currentLocation }) => {
    return isDirtyInPage && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (blocker.state === 'blocked' && showPrompt) {
      setConfirmNavigationModal(true);
    } else {
      blocker?.proceed?.();
    }
  }, [blocker, showPrompt]);

  const handleDiscardAndProceed = () => {
    setConfirmNavigationModal(false);
    blocker?.proceed?.();
    setShowPrompt(false);
    updateDirtyFieldsState(false);
  };

  const handleCloseNavigationModal = () => {
    setConfirmNavigationModal(false);
    blocker?.reset?.();
    setShowPrompt(true);
  };

  const fieldPoliciesArray = {
    client: useFieldArray({ name: 'client.policies', control: methods.control }),
    partner: useFieldArray({ name: 'partner.policies', control: methods.control }),
    joint: useFieldArray({ name: 'joint.policies', control: methods.control }),
  };

  const fieldEmployeeBenefitsArray = {
    client: useFieldArray({ name: 'client.employeeBenefits', control: methods.control }),
    partner: useFieldArray({ name: 'partner.employeeBenefits', control: methods.control }),
    joint: useFieldArray({ name: 'joint.employeeBenefits', control: methods.control }),
  };

  const fieldOtherBenefitsArray = {
    client: useFieldArray({ name: 'client.otherBenefits', control: methods.control }),
    partner: useFieldArray({ name: 'partner.otherBenefits', control: methods.control }),
    joint: useFieldArray({ name: 'joint.otherBenefits', control: methods.control }),
  };

  const fetchFieldArray = (type: 'policies' | 'employeeBenefits' | 'otherBenefits', tab: tabType) => {
    if (type === 'policies') return fieldPoliciesArray[tab];
    if (type === 'employeeBenefits') return fieldEmployeeBenefitsArray[tab];
    return fieldOtherBenefitsArray[tab];
  };

  const handleChangeTab = (_event: SyntheticEvent, value: tabType) => {
    setCurrentTab(value);
  };

  const handleChangeSection = (event: SyntheticEvent, value: string) => {
    setSelectSubSection(value);
  };

  const viewBenefitsAndInsuranceApi = useMutation({
    mutationFn: (payload: IViewFactFindRequest) => InvestorService().viewBenefitsAndInsurance(payload),

    onSuccess: (data: AxiosResponse<IBenefitsAndInsuranceResponseApiResponse>) => {
      const { data: response } = data;

      const benefitsAndInsuranceData = mapResponseToSchema(response.response, beneficiariesList);

      methods.reset(benefitsAndInsuranceData);
    },
  });

  const viewNomineesApi = useMutation({
    mutationFn: (payload: IViewFactFindRequest) => InvestorService().viewBeneficiaries(payload),

    onSuccess: (response: AxiosResponse<IViewfactFindBeneficiaryResponse>) => {
      const { data: responseData } = response;

      setBeneficiariesList(responseData?.beneficiaries);
      viewBenefitsAndInsuranceApi.mutateAsync({ clientID: state?.clientData?.clientID });
    },
  });

  useEffect(() => {
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      viewNomineesApi.mutateAsync({ clientID: state.clientData.clientID, insuranceBeneficiary: true, forExternalUse: true });
    }
    if (from === FactFindType.AI) {
      setBeneficiariesList(clientNomineesData?.beneficiaries);
      const benefitsAndInsuranceData = mapResponseToSchema(clientData?.response, clientNomineesData?.beneficiaries);
      reset(benefitsAndInsuranceData);
    }
  }, [reset, state.clientData.clientID, clientNomineesData, from, clientData]);

  const captureBenefitsAndInsuranceApi = useMutation({
    mutationFn: (payload: IBenefitsAndInsurancePayload) => InvestorService().captureBenefitsAndInsurance(payload),

    onSuccess: () => {
      methods.reset({}, { keepValues: true });
      viewNomineesApi.mutateAsync({ clientID: state?.clientData?.clientID, insuranceBeneficiary: true, forExternalUse: true });
      sourceControlMutateAsync({ clientID: state?.clientData?.clientID });
      blocker?.reset?.();
      setShowPrompt(false);
      enqueueSnackbar('Benefits and Insurance saved successfully', { variant: 'success' });
      updateFFErrorStore({ ffErrors: null });
    },

    onError: () => {
      isError.onTrue();
      setTimeout(() => {
        isError.onFalse();
      }, 3000);
    },
  });

  const captureBenefitsAndInsuranceApiAI = useMutation({
    mutationFn: (payload: IPatchAIInteractionSectionRequest) => AIInteractionService().patchClientData(payload),

    onSuccess: () => {
      methods.reset({}, { keepValues: true });
      refetch?.();
      sourceControlMutateAsync({ clientID: state.clientData.clientID });
      blocker?.reset?.();
      setShowPrompt(false);
      enqueueSnackbar('Benefits and Insurance saved successfully', { variant: 'success' });
    },

    onError: () => {
      isError.onTrue();
      setTimeout(() => {
        isError.onFalse();
      }, 3000);
    },
  });

  const onSubmit = (formData: beneficiariesAndInsuranceValidationSchemaType) => {
    const payload = { clientID: state?.clientData?.clientID, ...createPayloadBeneficiariesAndInsurance(formData) };
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      captureBenefitsAndInsuranceApi.mutateAsync(payload);
    }
    if (from === FactFindType.AI) {
      const aiPayload = {
        client_id: state.clientData.clientID,
        section: InteractionSubSectionType.benefits_and_insurance,
        data: { response: { employeeBenefits: { ...createPayloadBeneficiariesAndInsurance(formData) } } },
      };
      captureBenefitsAndInsuranceApiAI.mutateAsync(aiPayload);
    }
  };

  const getClientsOptions = clientPartnerJointTabs(true, false).map((tab) => ({
    ...tab,
    label: getLabel(tab, from === FactFindType.AI || isClient ? updatedClientPartnerDetails : clientPartnerDetails),
  }));

  const benefitsDetailsSections = [
    { label: 'Private Policies', key: ISubSectionType.private, icon: PrivateHealthInsuranceIcon, show: true },
    { label: 'Employee Benefits', key: ISubSectionType.employee, icon: BenefitsInsuranceIcon, show: true },
    { label: 'Other Benefits', key: ISubSectionType.other, icon: OthersAssetsIcon, show: true },
  ];

  return (
    <Box>
      {isTranscript.value && <Transcript section="InsuranceNBenefits" isTranscript={isTranscript} />}

      {isAllTranscripts.value && <AllTranscripts isAllTranscripts={isAllTranscripts} />}

      {!isAllTranscripts.value && !isTranscript.value && (
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          {viewBenefitsAndInsuranceApi.isPending || sourceControlPending ? (
            <FullPageSpinner />
          ) : (
            <>
              <FactFindHeading
                sectionName="Benefits & Insurance"
                isAllTranscripts={isAllTranscripts}
                isTranscript={isTranscript}
                hideButton={sourceControlData?.dataSourceMap?.InsuranceNBenefits === 'ManualInput'}
                hideAllButton={isClient || [FactFindType.AI, FactFindType.PARA_PLANNER].includes(from)}
              />
              <Stack direction="row" alignItems="center">
                {conflictData?.length > 0 && (
                  <Tooltip title="Click here to view all the conflicts">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => {
                        setConflictsDialog(true);
                      }}
                      sx={{ marginTop: '-12px' }}
                    >
                      <WarningRounded />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {fieldErrors?.length > 0 && <ErrorSection errors={fieldErrors} />}

              <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                <Autocomplete
                  options={
                    from === FactFindType.PARA_PLANNER ? getClientsOptions.filter((e) => !['Client', 'Partner', 'Joint'].includes(e.label)) : getClientsOptions
                  }
                  id="benefitsAutocomplete"
                  value={getClientsOptions.find((tab) => tab.value === currentTab)?.label ?? ''}
                  sx={{ width: 300, height: '56px' }}
                  onChange={(e, v) => handleChangeTab(e, v?.value)}
                  getOptionLabel={(option) => option?.label || option}
                  renderInput={(params) => <TextField {...params} />}
                  disableClearable={true}
                />

                <Tabs
                  sx={{
                    borderBottom: '2px solid rgba(3, 5, 7, 0.08)',
                    '& .MuiTabs-scrollButtons': {
                      opacity: 0.3,
                      visibility: 'visible',
                    },
                    '& .MuiTabs-scrollButtons.Mui-disabled': {
                      opacity: 0.3,
                    },
                  }}
                  value={selectedSubSection}
                  onChange={handleChangeSection}
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                >
                  {benefitsDetailsSections.map((section) => {
                    if (!section.show) return null;
                    return (
                      <Tab
                        key={section.key}
                        value={section.key}
                        id={section.label}
                        label={
                          <>
                            {section.label}
                            {section.errors && <AlertTriangleIcon />}
                          </>
                        }
                        iconPosition="start"
                      />
                    );
                  })}
                </Tabs>
              </Stack>

              <Stack spacing={1} marginTop={2} marginBottom={20}>
                {['client', 'partner', 'joint'].map(
                  (tab) =>
                    currentTab === tab && (
                      <Fragment key={tab}>
                        {selectedSubSection === ISubSectionType.private && (
                          <>
                            <Stack marginBottom={1.5}>
                              <Typography variant="h6" color="text.primary" id="privatePolicy">
                                Private Policies
                              </Typography>
                            </Stack>

                            {isReadOnly && fetchFieldArray('policies', tab).fields?.length === 0 && (
                              <Card>
                                <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                                  <EmptyContent description="No policies information has been captured as part of the fact-finding process." />
                                </Stack>
                              </Card>
                            )}

                            {fetchFieldArray('policies', tab).fields.map((policy, policyIndex) => (
                              <PolicyCard
                                key={policy.id}
                                policy={policy}
                                policyIndex={policyIndex}
                                tab={tab}
                                methods={methods}
                                beneficiariesList={beneficiariesList}
                                isClient={isClient}
                                isReadOnly={isReadOnly}
                                insuranceTypeOptions={insuranceTypeOptions}
                              />
                            ))}
                            {isClient || isReadOnly ? null : (
                              <Stack flexDirection="row" marginTop={1}>
                                <Button
                                  variant="text"
                                  color="primary"
                                  size="small"
                                  id="addPrivatePolicies"
                                  startIcon={<PlusIcon />}
                                  onClick={() => {
                                    fetchFieldArray('policies', tab).append(policiesDefault);
                                  }}
                                >
                                  Add Private Policies
                                </Button>
                              </Stack>
                            )}
                          </>
                        )}

                        {selectedSubSection === ISubSectionType.employee && (
                          <>
                            <Stack marginBottom={1.5}>
                              <Typography variant="h6" color="text.primary" id="employeeBenefits">
                                Employee Benefits
                              </Typography>
                            </Stack>

                            {isReadOnly && fetchFieldArray('employeeBenefits', tab).fields?.length === 0 && (
                              <Card>
                                <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                                  <EmptyContent description="No employee benefits information has been captured as part of the fact-finding process." />
                                </Stack>
                              </Card>
                            )}

                            {fetchFieldArray('employeeBenefits', tab).fields?.length > 0 && (
                              <SimpleFieldsTable
                                fieldArray={fetchFieldArray('employeeBenefits', tab).fields}
                                currentTab={`${currentTab}.employeeBenefits`}
                                onRemove={fetchFieldArray('employeeBenefits', tab).remove}
                                tableDetails={EMPLOYEE_BENEFITS_TABLE_STRUCTURE}
                                isClient={isClient}
                                isReadOnly={isReadOnly}
                              />
                            )}
                            {isClient || isReadOnly ? null : (
                              <Stack flexDirection="row" marginTop={1}>
                                <Button
                                  variant="text"
                                  color="primary"
                                  id="addEmployeeBenefits"
                                  size="small"
                                  startIcon={<PlusIcon />}
                                  onClick={() => {
                                    fetchFieldArray('employeeBenefits', tab).append(employeeBenefitsDefault);
                                  }}
                                >
                                  Add Employee Benefits
                                </Button>
                              </Stack>
                            )}
                          </>
                        )}

                        {selectedSubSection === ISubSectionType.other && (
                          <>
                            <Stack marginBottom={1.5}>
                              <Typography variant="h6" color="text.primary" id="otherBenefits">
                                Other Benefits
                              </Typography>
                            </Stack>

                            {isReadOnly && fetchFieldArray('otherBenefits', tab).fields?.length === 0 && (
                              <Card>
                                <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                                  <EmptyContent description="No other benefits information has been captured as part of the fact-finding process." />
                                </Stack>
                              </Card>
                            )}

                            {fetchFieldArray('otherBenefits', tab).fields?.length > 0 && (
                              <SimpleFieldsTable
                                fieldArray={fetchFieldArray('otherBenefits', tab).fields}
                                currentTab={`${currentTab}.otherBenefits`}
                                onRemove={fetchFieldArray('otherBenefits', tab).remove}
                                tableDetails={OTHER_BENEFITS_TABLE_STRUCTURE(insuranceTypeOptions)}
                                isClient={isClient}
                                isReadOnly={isReadOnly}
                              />
                            )}
                            {isClient || isReadOnly ? null : (
                              <Stack flexDirection="row" marginTop={1}>
                                <Button
                                  variant="text"
                                  color="primary"
                                  id="addOtherBenefitsButton"
                                  size="small"
                                  startIcon={<PlusIcon />}
                                  onClick={() => {
                                    fetchFieldArray('otherBenefits', tab).append(otherBenefitsDefault);
                                  }}
                                >
                                  Add Other Benefits
                                </Button>
                              </Stack>
                            )}
                          </>
                        )}
                      </Fragment>
                    )
                )}
              </Stack>
            </>
          )}

          {isClient ? null : (
            <>
              {[FactFindType.FACT_FIND, FactFindType.AI].includes(from) && (
                <FactFindFooter from={from} hideNextStep={from === FactFindType.AI} isReadOnly={isReadOnly} />
              )}
              <SaveDialog open={captureBenefitsAndInsuranceApi.isPending} />
              <SuccessDialog open={isSuccess.value} />
              <ErrorDialog open={isError.value} />
              <ConfirmNavigationDialog open={confirmNavigationModal} handleClose={handleCloseNavigationModal} data={{ handleDiscardAndProceed, blocker }} />
              <ConflictsDialog
                open={conflictsDialog}
                handleClose={() => setConflictsDialog(false)}
                tableData={conflictData || []}
                isPending={false}
                title={'Benefits & Insurance'}
                refetch={refetch}
              />
            </>
          )}
        </FormProvider>
      )}
    </Box>
  );
};
